import { gql, NetworkStatus, useApolloClient, useMutation, useQuery } from '@apollo/client'
import cx from 'classnames'
import PageComponent from 'components/Dashboard/PageComponent'
import RawJsonDisplay from 'components/Dashboard/RawJsonDisplay'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { Link } from 'gatsby'
import * as React from 'react'
import { FaSync } from 'react-icons/fa'
import { DashboardTitle } from './DashboardTitleContext'
import openAttachment from './openAttachment'
import { EmailQueueQuery, EmailQueueQueryVariables } from './__generated__/EmailQueue'
import { EmailQueueQuery_outboundMailQueue } from './__generated__/EmailQueueQuery'
const formatDate = (dateString: string) => new Date(dateString).toLocaleString()

const query = gql`
  query EmailQueueQuery($queueName: String!) {
    outboundMailQueue(queueName: $queueName) {
      id
      status
      submittedAt
      template
      templateParamsJson
      recipients {
        type
        email
      }
      attachments {
        name
      }
      item {
        __typename

        ... on Kit {
          id
          createdAt
          status
          thirdPartyResults {
            associateId
            consentGiven
          }
        }
        ... on Order {
          id
          createdAt
          paymentStatus
        }
      }
    }
  }
`

const cancelMutation = gql`
  mutation AbortOutboundEmail($id: ID!, $reason: String!) {
    abortOutboundEmail(id: $id, reason: $reason) {
      success
      message
      outboundEmail {
        id
        status
      }
    }
  }
`

const sendMutation = gql`
  mutation SendOutboundEmail($id: ID!) {
    sendOutboundEmail(id: $id) {
      success
      message
      outboundEmail {
        id
        status
      }
    }
  }
`

interface EmailRowProps {
  email: EmailQueueQuery_outboundMailQueue
  showMandrillState?: boolean
}

const EmailRow = ({ email }: EmailRowProps) => {
  const [cancelEmail, { loading: canceling }] = useMutation(cancelMutation)
  const [sendEmail, { loading: sending }] = useMutation(sendMutation)
  const client = useApolloClient()

  const onCancel = async (id: string) => {
    const reason = window.prompt('Please explain why this email should not be sent')

    const result = await cancelEmail({ variables: { id, reason } })

    console.log('RES', result)
  }

  const onSend = async (id: string) => {
    const result = await sendEmail({ variables: { id } })

    console.log('RES', result)
  }

  return (
    <tr key={email.id}>
      <td>
        {formatDate(email.submittedAt)}
        {email.status === 'AWAITING_CONFIRMATION' && (
          <p>
            <strong>Waiting: </strong>
            {formatDistanceToNow(parseISO(email.submittedAt))}
          </p>
        )}
        {email.status === 'SENDING' && <p>Sending to Mandrill...</p>}
      </td>
      <td>
        {email.item.__typename === 'Kit' && (
          <>
            <Link to={`/dashboard/kits?kitId=${email.item.id}`}>{email.item.id}</Link>
            {email.status === 'AWAITING_CONFIRMATION' && (
              <>
                <br />
                Status: {email.item.status}
                {email.item.thirdPartyResults && (
                  <p>
                    <strong>B2B: </strong>
                    {email.item.thirdPartyResults.associateId}{' '}
                    <span
                      className={email.item.thirdPartyResults.consentGiven ? 'has-text-success' : 'has-text-warning'}
                    >
                      {email.item.thirdPartyResults.consentGiven
                        ? 'Sharing consent given'
                        : 'Sharing consent NOT given'}
                    </span>
                  </p>
                )}
              </>
            )}
          </>
        )}

        {email.item.__typename === 'Order' && <p>{email.item.id}</p>}
      </td>
      <td>
        Template: <strong>{email.template}</strong>
        {email.status === 'AWAITING_CONFIRMATION' && (
          <>
            {email.recipients.map(({ type, email }) => (
              <p key={email}>
                <strong>{type}: </strong>
                {email}
              </p>
            ))}
            <br />
            <RawJsonDisplay json={email.templateParamsJson} />

            {email.attachments.map((attach) => (
              <p key={attach.name}>
                Attachment: {attach.name}
                <button
                  className="button is-small is-text"
                  onClick={() => {
                    openAttachment(client, email.id, attach)
                  }}
                >
                  Open
                </button>
              </p>
            ))}
          </>
        )}
      </td>
      <td>
        {email.status === 'AWAITING_CONFIRMATION' && (
          <div className="buttons">
            <button
              className={cx('button is-outlined is-primary', {
                'is-loading': sending,
              })}
              disabled={canceling || sending}
              onClick={() => onSend(email.id)}
            >
              Send
            </button>
            <button
              className={cx('button is-outlined', {
                'is-loading': canceling,
              })}
              disabled={canceling || sending}
              onClick={() => onCancel(email.id)}
            >
              Cancel
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

interface QueueProps {
  queueName: string
  showMandrillState?: boolean
  title: (count: number) => React.ReactNode
}

const Queue = ({ queueName, title }: QueueProps) => {
  const { loading, error, data, refetch, networkStatus } = useQuery<EmailQueueQuery, EmailQueueQueryVariables>(query, {
    variables: { queueName },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  if (error) {
    return <p>ERROR {error.message}</p>
  }

  if (loading && !data) {
    return <p>Loading...</p>
  }

  return (
    <>
      <nav className="level has-width-unset">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5">{title(data.outboundMailQueue.length)}</p>
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
            <button
              className={cx('button is-info is-outlined', {
                'is-loading': networkStatus === NetworkStatus.refetch,
              })}
              onClick={() => refetch()}
            >
              <span className="icon is-small">
                <FaSync />
              </span>
            </button>
          </div>
        </div>
      </nav>

      <table className="table is-bordered is-fullwidth" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th>Date</th>
            <th>Item</th>
            <th>Email details</th>
            <th style={{ width: '12rem' }}>&nbsp;</th>
          </tr>
        </thead>

        <tbody>
          {data.outboundMailQueue.map((email) => (
            <EmailRow key={email.id} email={email} showMandrillState />
          ))}
        </tbody>
      </table>
    </>
  )
}

const EmailQueue: PageComponent = () => {
  return (
    <div className="container">
      <DashboardTitle title="Email Queue" />

      <Queue
        queueName="AutoEmails"
        title={(count) => (
          <>
            <strong>{count}</strong> emails will be auto-sent
          </>
        )}
      />

      <Queue
        queueName="UnsentEmails"
        showMandrillState
        title={(count) => (
          <>
            <strong>{count}</strong> emails awaiting Mandrill response
          </>
        )}
      />

      <Queue queueName="RecentEmails" showMandrillState title={() => <>Recently sent emails</>} />
    </div>
  )
}

EmailQueue.displayName = 'Email Queue'
EmailQueue.path = '/email-queue'
EmailQueue.allowedGroups = ['dashboard', 'rapid_photo_verifiers']

export default EmailQueue
